// ============================================================
// ANSICHTSSACHE-AGENT – Betrags- und Zahlenfeld
// ============================================================
// Ein `<input type="number">` ist fuer Geld die falsche Wahl.
// Es nimmt nur die Schreibweise der Maschine an — „1234.56“ —,
// und was der Browser daraus macht, haengt an SEINER Spracheinstellung,
// nicht an unserer. Das Ergebnis stand im Bestand schwarz auf
// weiss: ein Volumenfeld mit dem Platzhalter „50.000“, das als
// Zahl fuenfzig entgegennahm, und daneben ein Betragsfeld mit
// dem Platzhalter „49,99“, in das genau dieses Komma nicht
// hineinging.
//
// Hier ist es getrennt:
//   - SICHTBAR steht ein Textfeld in deutscher Schreibweise. Es
//     nimmt Komma, Tausenderpunkt, Eurozeichen und alles, was
//     beim Kopieren aus einer Tabelle mitkommt.
//   - UNSICHTBAR daneben liegt dasselbe als Zahl mit Punkt. Das
//     geht ins Formular, und darum mussten die Server-Actions
//     kein Stueck angefasst werden: `parseFloat` und
//     `new Decimal()` bekommen weiter genau das, was sie kennen.
//
// Beim Verlassen des Feldes wird die Eingabe neu geschrieben —
// wer „1234.5“ tippt, sieht danach „1.234,50“ und weiss, dass
// die Zahl verstanden wurde.
// ============================================================

"use client";

import { useState } from "react";
import { Input, type InputProps } from "./input";
import { formatAmount, formatDecimal, parseDecimal, toCanonical } from "@/lib/number-format";

type Basis = Omit<InputProps, "value" | "defaultValue" | "type" | "onChange">;

function NumberField({
  name,
  initial,
  format,
  onBlur,
  onValueChange,
  ...rest
}: Basis & {
  name: string;
  initial: string;
  format: (value: number | null) => string;
  /** Fuer Anzeigen, die sofort mitrechnen sollen. */
  onValueChange?: (value: number | null) => void;
}) {
  const [text, setText] = useState(initial);
  const value = parseDecimal(text);

  return (
    <>
      <Input
        {...rest}
        // Die Beschriftung haengt sonst am `name`, den hier das
        // verborgene Feld traegt.
        id={rest.id ?? name}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onValueChange?.(parseDecimal(e.target.value));
        }}
        onBlur={(e) => {
          setText(format(value));
          onBlur?.(e);
        }}
      />
      <input type="hidden" name={name} value={toCanonical(value)} />
    </>
  );
}

/**
 * Ein Betrag in Euro. Herein und hinaus gehen CENT — so liegt
 * der Wert im Datenmodell, und so entfaellt das Umrechnen an
 * jeder Aufrufstelle.
 */
export function AmountInput({
  defaultCents,
  onValueChange,
  ...rest
}: Basis & {
  name: string;
  defaultCents?: number | bigint | null;
  /**
   * Fuer Anzeigen, die waehrend des Tippens mitrechnen. Herein
   * gehen Cent, hinaus gehen Cent — das Feld ist die einzige
   * Stelle, an der ueberhaupt in Euro gerechnet wird.
   */
  onValueChange?: (cents: number | null) => void;
}) {
  return (
    <NumberField
      {...rest}
      initial={
        defaultCents === null || defaultCents === undefined ? "" : formatAmount(defaultCents)
      }
      format={(value) => (value === null ? "" : formatAmount(Math.round(value * 100)))}
      onValueChange={
        onValueChange
          ? (value) => onValueChange(value === null ? null : Math.round(value * 100))
          : undefined
      }
    />
  );
}

/**
 * Eine Dezimalzahl ohne Waehrung — Stunden, Prozent, Mengen.
 * `decimals` ist die groesste Zahl von Nachkommastellen, die
 * stehen bleibt; angezeigt werden nur die noetigen.
 */
export function DecimalInput({
  defaultValue,
  decimals = 2,
  ...rest
}: Basis & {
  name: string;
  defaultValue?: number | string | null;
  decimals?: number;
  /** Fuer Anzeigen, die waehrend des Tippens mitrechnen. */
  onValueChange?: (value: number | null) => void;
}) {
  return (
    <NumberField
      {...rest}
      initial={
        defaultValue === null || defaultValue === undefined || defaultValue === ""
          ? ""
          : formatDecimal(defaultValue, { max: decimals })
      }
      format={(value) => formatDecimal(value, { max: decimals })}
    />
  );
}
